import * as R from 'ramda';
import React from 'react';
import {Observable, of} from 'rxjs';

/** Converts a value to an observable if it is not already an observable, otherwise just returns the given observable. */
export function toObservable(value) {
  return value instanceof Observable ? value : of(value);
}

/** A higher order component to handle subscriptions to an RXJS observable.
 * Mostly used to load data from the backend.
 * Passes values emitted by the given observable to the sub component.
 *
 * parameters:
 * observableOrFunction: an observable or a function taking props and returning an observable
 * itemKey: What prop to pass the values returned by the observable into
 * WrappedComponent: the component to wrap
 * ErrorHandlerComponent: on optional error handler component to use when the observable errors
 *
 * Passes the latest emited item to the wrapped component alongside the
 * prop reloadSubscription that can be used to resubscribe to the observable.
 *
 */
const subscribeTo = R.curry(function subscribeTo(observableOrFunction, itemKey, WrappedComponent, ErrorHandlerComponent = null) {
  return (props) => {
    return <SubscribeTo observable={getObservable(observableOrFunction, props)}
      errorHandler={ErrorHandlerComponent && ((subscriptionProps) => <ErrorHandlerComponent {...subscriptionProps} {...props} />)}>
      {(item, reloadSubscription) => {
        const subscriptionProps = {
          [itemKey]: item,
          reloadSubscription
        };
        return <WrappedComponent {...subscriptionProps} {...props} />;
      }}
    </SubscribeTo>;
  };
});

function getObservable(observableOrFunction, props) {
  const isObservable = R.is(Observable, observableOrFunction);
  if (isObservable) return observableOrFunction;
  //Call the function
  return observableOrFunction(props);
}

/** A wrapper component that handles subscription to an observable
 *
 * props:
 * observable: Any RXJS observable
 * errorHandler: an optional component to show when the observable emits an error
 * children: A function taking the latest emited item and a reload function
 *           as parameters. The reload function can be used to resubscribe
 *           to the observable.
 *
 */
export class SubscribeTo extends React.Component {
  state = {
    hasItem: false,
  };
  componentDidMount() {
    this.subscribe();
  }
  componentDidUpdate(prevProps) {
    if (prevProps.observable !== this.props.observable) {
      this.reload();
    }
  }
  componentWillUnmount() {
    this.subscription.unsubscribe();
  }
  reload = () => {
    this.subscription.unsubscribe();
    this.subscribe();
  };
  subscribe = () => {
    this.subscription = this.props.observable.subscribe((item) => {
      this.setState({
        item,
        hasItem: true,
        error: null
      });
    }, (error) => {
      this.setState({
        item: null,
        hasItem: false,
        error
      });
    });
  };
  render() {
    if (this.state.error && this.props.errorHandler) {
      const ErrorHandlerComponent = this.props.errorHandler;
      return <ErrorHandlerComponent error={this.state.error} reloadSubscription={this.reload} />;
    }

    if (this.state.hasItem) {
      return this.props.children(this.state.item, this.reload);
    }

    return null;
  }
}

export default subscribeTo;



// WEBPACK FOOTER //
// ./modules/issFrontend/utils/subscriptionUtils.js